import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiArrowRight,
  FiChevronUp,
  FiChevronDown,
  FiClock,
  FiFileText,
  FiPlus,
  FiMinus,
  FiCheck,
  FiCheckCircle,
  FiAlertCircle,
  FiMaximize2,
  FiMinimize2,
  FiGitBranch,
  FiLayers,
  FiColumns,
  FiAlignLeft,
  FiX,
  FiZap,
  FiRefreshCw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { documentsAPI } from '../../services/api';
import { formatDateTime, formatDateMedium } from '../../utils/dateFormat';
import './VersionCompare.scss';

const MAX_SELECTED = 4;
const ORDER_LETTERS = ['A', 'B', 'C', 'D'];

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  return formatDateTime(dateStr);
}

function formatDateShort(dateStr) {
  return formatDateMedium(dateStr);
}

function getInitials(user) {
  if (!user) return '?';
  const first = user.firstName?.[0] || '';
  const last = user.lastName?.[0] || '';
  return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
}

function getFullName(user) {
  if (!user) return 'Unknown';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown';
}

function pairKey(a, b) {
  return `${a}-${b}`;
}

/**
 * Parse the raw diffHtml from the backend into structured change blocks
 * that are human-readable (no code jargon).
 */
function parseDiffHtml(rawHtml) {
  if (!rawHtml) return { blocks: [], stats: { added: 0, removed: 0, modified: 0 } };

  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  const container = doc.querySelector('.document-diff') || doc.body;

  const segments = [];
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text) segments.push({ type: 'unchanged', text });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const cls = node.className;
      const text = node.textContent;
      if (cls === 'deleted') {
        if (text) segments.push({ type: 'removed', text });
      } else if (cls === 'added') {
        if (text) segments.push({ type: 'added', text });
      } else if (cls === 'unchanged') {
        if (text) segments.push({ type: 'unchanged', text });
      } else {
        Array.from(node.childNodes).forEach(processNode);
      }
    }
  };
  Array.from(container.childNodes).forEach(processNode);

  if (segments.length === 0) return { blocks: [], stats: { added: 0, removed: 0, modified: 0 } };

  const changeIndices = segments
    .map((s, i) => (s.type !== 'unchanged' ? i : -1))
    .filter((i) => i !== -1);

  if (changeIndices.length === 0) {
    return { blocks: [], stats: { added: 0, removed: 0, modified: 0 } };
  }

  // Group changes separated by large unchanged gaps
  const MIN_GAP_CHARS = 120;
  const groups = [];
  let currentGroup = [changeIndices[0]];

  for (let k = 1; k < changeIndices.length; k++) {
    const prevEnd = changeIndices[k - 1];
    const nextStart = changeIndices[k];

    let gapChars = 0;
    for (let j = prevEnd + 1; j < nextStart; j++) {
      if (segments[j].type === 'unchanged') {
        gapChars += segments[j].text.length;
      }
    }

    if (gapChars < MIN_GAP_CHARS) {
      currentGroup.push(changeIndices[k]);
    } else {
      groups.push(currentGroup);
      currentGroup = [changeIndices[k]];
    }
  }
  groups.push(currentGroup);

  const CONTEXT_CHARS = 180;

  const blocks = groups.map((g, idx) => {
    const minIdx = g[0];
    const maxIdx = g[g.length - 1];

    let contextBefore = '';
    for (let i = minIdx - 1; i >= 0; i--) {
      if (segments[i].type === 'unchanged') {
        contextBefore = segments[i].text + contextBefore;
        if (contextBefore.length >= CONTEXT_CHARS) break;
      }
    }
    if (contextBefore.length > CONTEXT_CHARS) {
      const trimmed = contextBefore.slice(-CONTEXT_CHARS);
      const spaceIdx = trimmed.indexOf(' ');
      contextBefore = '…' + (spaceIdx > 0 ? trimmed.slice(spaceIdx + 1) : trimmed);
    }

    let contextAfter = '';
    for (let i = maxIdx + 1; i < segments.length; i++) {
      if (segments[i].type === 'unchanged') {
        contextAfter += segments[i].text;
        if (contextAfter.length >= CONTEXT_CHARS) break;
      }
    }
    if (contextAfter.length > CONTEXT_CHARS) {
      const trimmed = contextAfter.slice(0, CONTEXT_CHARS);
      const lastSpace = trimmed.lastIndexOf(' ');
      contextAfter = (lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed) + '…';
    }

    const changedSegments = segments.slice(minIdx, maxIdx + 1);
    const removedText = changedSegments
      .filter((s) => s.type === 'removed')
      .map((s) => s.text)
      .join('');
    const addedText = changedSegments
      .filter((s) => s.type === 'added')
      .map((s) => s.text)
      .join('');

    return {
      id: idx,
      contextBefore: contextBefore.trim(),
      contextAfter: contextAfter.trim(),
      removedText: removedText.trim(),
      addedText: addedText.trim(),
    };
  });

  const validBlocks = blocks.filter((b) => b.removedText || b.addedText);

  const stats = {
    added: validBlocks.filter((b) => b.addedText && !b.removedText).length,
    removed: validBlocks.filter((b) => b.removedText && !b.addedText).length,
    modified: validBlocks.filter((b) => b.addedText && b.removedText).length,
  };

  return { blocks: validBlocks, stats };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function UserAvatar({ user, size = 'md' }) {
  return <div className={`vx-avatar vx-avatar--${size}`}>{getInitials(user)}</div>;
}

function StatPills({ stats, t, compact }) {
  return (
    <div className={`vx-stat-pills ${compact ? 'vx-stat-pills--compact' : ''}`}>
      {stats.modified > 0 && (
        <span className="vx-pill vx-pill--modified">
          <FiLayers size={11} /> {stats.modified}
        </span>
      )}
      {stats.added > 0 && (
        <span className="vx-pill vx-pill--added">
          <FiPlus size={11} /> {stats.added}
        </span>
      )}
      {stats.removed > 0 && (
        <span className="vx-pill vx-pill--removed">
          <FiMinus size={11} /> {stats.removed}
        </span>
      )}
      {stats.modified === 0 && stats.added === 0 && stats.removed === 0 && (
        <span className="vx-pill vx-pill--neutral">
          <FiCheck size={11} /> 0
        </span>
      )}
    </div>
  );
}

function VersionCard({ ver, isSelected, orderIdx, isLatest, disabled, onToggle, t }) {
  return (
    <button
      type="button"
      className={`vx-vcard ${isSelected ? 'vx-vcard--selected' : ''} ${disabled ? 'vx-vcard--disabled' : ''}`}
      onClick={onToggle}
      aria-pressed={isSelected}
      aria-label={t('versionCompare.versionN', { n: ver.versionNumber })}
    >
      <div className="vx-vcard__top">
        <span className="vx-vcard__num">{t('versionCompare.vShort', { n: ver.versionNumber })}</span>
        {isLatest && <span className="vx-vcard__latest">{t('versionCompare.current')}</span>}
        <span className={`vx-vcard__check ${isSelected ? 'vx-vcard__check--on' : ''}`}>
          {isSelected ? <span className="vx-vcard__order">{ORDER_LETTERS[orderIdx]}</span> : <FiPlus size={13} />}
        </span>
      </div>
      <div className="vx-vcard__author">
        <UserAvatar user={ver.createdBy} size="xs" />
        <span className="vx-vcard__author-name">{getFullName(ver.createdBy)}</span>
      </div>
      <div className="vx-vcard__date">
        <FiClock size={10} />
        {formatDateShort(ver.createdAt)}
      </div>
    </button>
  );
}

function SkeletonCard() {
  return (
    <div className="vx-skeleton-card" aria-hidden="true">
      <div className="vx-skeleton vx-skeleton--title" />
      <div className="vx-skeleton vx-skeleton--line" />
      <div className="vx-skeleton vx-skeleton--line vx-skeleton--short" />
      <div className="vx-skeleton vx-skeleton--block" />
    </div>
  );
}

function ChangeCard({ block, num, active, viewMode, fromV, toV, onActivate, cardRef, animIdx, t }) {
  const [collapsed, setCollapsed] = useState(false);

  const changeType =
    block.addedText && block.removedText ? 'modified' : block.addedText ? 'added' : 'removed';

  const typeIcon =
    changeType === 'added' ? <FiPlus size={13} /> : changeType === 'removed' ? <FiMinus size={13} /> : <FiLayers size={13} />;

  return (
    <article
      className={`vx-card vx-card--${changeType} ${active ? 'vx-card--active' : ''}`}
      ref={cardRef}
      onClick={onActivate}
      style={{ '--anim-idx': Math.min(animIdx, 8) }}
    >
      <header className="vx-card__header">
        <div className="vx-card__header-left">
          <span className={`vx-card__type vx-card__type--${changeType}`}>{typeIcon}</span>
          <span className="vx-card__title">{t('versionCompare.changeTitle', { num })}</span>
          <span className={`vx-badge vx-badge--${changeType}`}>
            {t(`versionCompare.${changeType}_badge`)}
          </span>
        </div>
        <button
          type="button"
          className="vx-card__collapse"
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((v) => !v);
          }}
          aria-label={collapsed ? t('versionCompare.expand') : t('versionCompare.collapse')}
        >
          {collapsed ? <FiChevronDown size={15} /> : <FiChevronUp size={15} />}
        </button>
      </header>

      {!collapsed && (
        <div className="vx-card__body">
          {viewMode === 'unified' ? (
            <p className="vx-flow">
              {block.contextBefore && <span className="vx-flow__ctx">{block.contextBefore} </span>}
              {block.removedText && <del className="vx-flow__del">{block.removedText}</del>}
              {block.removedText && block.addedText && ' '}
              {block.addedText && <ins className="vx-flow__ins">{block.addedText}</ins>}
              {block.contextAfter && <span className="vx-flow__ctx"> {block.contextAfter}</span>}
            </p>
          ) : (
            <div className="vx-split">
              <div className="vx-split__pane vx-split__pane--old">
                <div className="vx-split__label">
                  <FiMinus size={11} />
                  <span>
                    {t('versionCompare.versionN', { n: fromV })} · {t('versionCompare.beforeLabel')}
                  </span>
                </div>
                <p className="vx-split__text">
                  {block.contextBefore && <span className="vx-flow__ctx">{block.contextBefore} </span>}
                  {block.removedText ? (
                    <del className="vx-flow__del">{block.removedText}</del>
                  ) : (
                    <span className="vx-split__empty">{t('versionCompare.nothingHere')}</span>
                  )}
                  {block.contextAfter && <span className="vx-flow__ctx"> {block.contextAfter}</span>}
                </p>
              </div>
              <div className="vx-split__pane vx-split__pane--new">
                <div className="vx-split__label">
                  <FiPlus size={11} />
                  <span>
                    {t('versionCompare.versionN', { n: toV })} · {t('versionCompare.afterLabel')}
                  </span>
                </div>
                <p className="vx-split__text">
                  {block.contextBefore && <span className="vx-flow__ctx">{block.contextBefore} </span>}
                  {block.addedText ? (
                    <ins className="vx-flow__ins">{block.addedText}</ins>
                  ) : (
                    <span className="vx-split__empty">{t('versionCompare.nothingHere')}</span>
                  )}
                  {block.contextAfter && <span className="vx-flow__ctx"> {block.contextAfter}</span>}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

function VersionCompare() {
  const { id: contractId, documentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [docInfo, setDocInfo] = useState(null);

  const [selected, setSelected] = useState(() => {
    const vsParam = searchParams.get('versions');
    if (vsParam) {
      const nums = [...new Set(vsParam.split(',').map((n) => parseInt(n)).filter((n) => !isNaN(n)))];
      return nums.slice(0, MAX_SELECTED);
    }
    const v1 = parseInt(searchParams.get('v1'));
    const v2 = parseInt(searchParams.get('v2'));
    if (!isNaN(v1) && !isNaN(v2) && v1 !== v2) return [v1, v2];
    return [];
  });

  const [diffs, setDiffsState] = useState({});
  const diffsRef = useRef({});
  const setDiffs = useCallback((updater) => {
    setDiffsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      diffsRef.current = next;
      return next;
    });
  }, []);

  const [viewMode, setViewMode] = useState('unified');
  const [activeIdx, setActiveIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const changeRefs = useRef({});
  const stepRefs = useRef({});

  // ── Load versions + document info ─────────────────────────────────────────
  useEffect(() => {
    if (!contractId || !documentId) return;
    setVersionsLoading(true);
    Promise.all([
      documentsAPI.getVersions(contractId, documentId),
      documentsAPI.get(contractId, documentId),
    ])
      .then(([versionsRes, docRes]) => {
        const vList = versionsRes.data.versions || [];
        setVersions(vList);
        setDocInfo(docRes.data.document);
        setSelected((prev) => {
          const valid = prev.filter((n) => vList.some((v) => v.versionNumber === n));
          if (valid.length >= 2) return valid;
          if (vList.length >= 2) {
            const sorted = [...vList].sort((a, b) => a.versionNumber - b.versionNumber);
            return [
              sorted[sorted.length - 2].versionNumber,
              sorted[sorted.length - 1].versionNumber,
            ];
          }
          return valid;
        });
      })
      .catch(() => toast.error(t('versionCompare.loadFailed')))
      .finally(() => setVersionsLoading(false));
  }, [contractId, documentId, t]);

  // ── Derived: sorted selection + comparison pairs ───────────────────────────
  const sortedSelected = useMemo(() => [...selected].sort((a, b) => a - b), [selected]);

  const pairs = useMemo(() => {
    const p = [];
    for (let i = 0; i < sortedSelected.length - 1; i++) {
      p.push([sortedSelected[i], sortedSelected[i + 1]]);
    }
    return p;
  }, [sortedSelected]);

  const pairsSignature = pairs.map((p) => p.join('-')).join(',');

  // ── Sync URL ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (sortedSelected.length >= 2) {
      setSearchParams({ versions: sortedSelected.join(',') }, { replace: true });
    }
  }, [sortedSelected, setSearchParams]);

  // ── Fetch diffs for pairs (cached per pair) ───────────────────────────────
  const loadPair = useCallback(
    (a, b) => {
      const key = pairKey(a, b);
      setDiffs((prev) => ({ ...prev, [key]: { status: 'loading' } }));
      documentsAPI
        .compare(contractId, documentId, a, b)
        .then((res) => {
          const parsed = parseDiffHtml(res.data.diffHtml || '');
          setDiffs((prev) => ({ ...prev, [key]: { status: 'ready', ...parsed } }));
        })
        .catch((err) => {
          setDiffs((prev) => ({
            ...prev,
            [key]: { status: 'error', error: err.response?.data?.error },
          }));
        });
    },
    [contractId, documentId, setDiffs]
  );

  useEffect(() => {
    pairs.forEach(([a, b]) => {
      if (!diffsRef.current[pairKey(a, b)]) loadPair(a, b);
    });
  }, [pairsSignature, loadPair]); // eslint-disable-line

  // ── Steps + flat change list (for global navigation) ─────────────────────
  const steps = useMemo(
    () =>
      pairs.map(([a, b], i) => ({
        key: pairKey(a, b),
        from: a,
        to: b,
        index: i,
        diff: diffs[pairKey(a, b)],
      })),
    [pairs, diffs]
  );

  const flatChanges = useMemo(() => {
    const list = [];
    steps.forEach((step) => {
      if (step.diff?.status === 'ready') {
        step.diff.blocks.forEach((block) => {
          list.push({ refKey: `${step.key}:${block.id}` });
        });
      }
    });
    return list;
  }, [steps]);

  const totalChanges = flatChanges.length;
  const anyLoading = steps.some((s) => !s.diff || s.diff.status === 'loading');

  const totalStats = useMemo(() => {
    const agg = { added: 0, removed: 0, modified: 0 };
    steps.forEach((s) => {
      if (s.diff?.status === 'ready') {
        agg.added += s.diff.stats.added;
        agg.removed += s.diff.stats.removed;
        agg.modified += s.diff.stats.modified;
      }
    });
    return agg;
  }, [steps]);

  useEffect(() => {
    if (activeIdx >= totalChanges && totalChanges > 0) setActiveIdx(0);
  }, [totalChanges, activeIdx]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const navigateTo = useCallback(
    (idx) => {
      if (idx < 0 || idx >= flatChanges.length) return;
      setActiveIdx(idx);
      const el = changeRefs.current[flatChanges[idx].refKey];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [flatChanges]
  );

  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        navigateTo(activeIdx + 1);
      }
      if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        navigateTo(activeIdx - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeIdx, navigateTo]);

  const scrollToStep = (key) => {
    const el = stepRefs.current[key];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Selection handlers ─────────────────────────────────────────────────────
  const toggleVersion = (n) => {
    if (selected.includes(n)) {
      setSelected((prev) => prev.filter((x) => x !== n));
      return;
    }
    if (selected.length >= MAX_SELECTED) {
      toast.info(t('versionCompare.maxReached', { max: MAX_SELECTED }));
      return;
    }
    setSelected((prev) => [...prev, n]);
  };

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => a.versionNumber - b.versionNumber),
    [versions]
  );
  const latestVersionNum = sortedVersions[sortedVersions.length - 1]?.versionNumber;

  const selectLatestChange = () => {
    if (sortedVersions.length < 2) return;
    setSelected([
      sortedVersions[sortedVersions.length - 2].versionNumber,
      sortedVersions[sortedVersions.length - 1].versionNumber,
    ]);
  };

  const selectFullSpan = () => {
    if (sortedVersions.length < 2) return;
    setSelected([sortedVersions[0].versionNumber, latestVersionNum]);
  };

  const versionInfo = (n) => versions.find((v) => v.versionNumber === n);

  // ── Running change number offset per step ─────────────────────────────────
  let runningIdx = 0;
  const stepStartIdx = steps.map((s) => {
    const start = runningIdx;
    if (s.diff?.status === 'ready') runningIdx += s.diff.blocks.length;
    return start;
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={`vx-root ${fullscreen ? 'vx-root--fullscreen' : ''}`}>
      {/* ── Top Bar ── */}
      <header className="vx-topbar">
        <div className="vx-topbar__left">
          {/* Back goes to the version list this comparison was opened from —
              the contract's Documents tab with this document's history
              expanded — never to the editor. Compare can be reached from the
              editor's version panel too, and dropping the user into an edit
              session they didn't ask for loses the contract context. */}
          <Link
            to={`/contracts/${contractId}`}
            state={{ openTab: 'documents', openVersionsDocId: documentId }}
            className="vx-back-btn"
          >
            <FiArrowLeft size={15} />
            <span>{t('versionCompare.backToVersions')}</span>
          </Link>
          <div className="vx-topbar__divider" />
          <div className="vx-topbar__title">
            <span className="vx-topbar__doc-icon">
              <FiFileText size={15} />
            </span>
            <div>
              <div className="vx-topbar__doc-name">
                {docInfo?.title || t('documentEditor.newDocument')}
              </div>
              <div className="vx-topbar__subtitle">{t('versionCompare.versionComparison')}</div>
            </div>
          </div>
        </div>
        <div className="vx-topbar__right">
          <div className="vx-segmented" role="group" aria-label={t('versionCompare.versionComparison')}>
            <button
              type="button"
              className={`vx-segmented__btn ${viewMode === 'unified' ? 'vx-segmented__btn--active' : ''}`}
              onClick={() => setViewMode('unified')}
            >
              <FiAlignLeft size={13} />
              <span>{t('versionCompare.unifiedView')}</span>
            </button>
            <button
              type="button"
              className={`vx-segmented__btn ${viewMode === 'split' ? 'vx-segmented__btn--active' : ''}`}
              onClick={() => setViewMode('split')}
            >
              <FiColumns size={13} />
              <span>{t('versionCompare.splitView')}</span>
            </button>
          </div>
          <button
            type="button"
            className="vx-icon-btn"
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? t('versionCompare.exitFullscreen') : t('versionCompare.fullscreen')}
            title={fullscreen ? t('versionCompare.exitFullscreen') : t('versionCompare.fullscreen')}
          >
            {fullscreen ? <FiMinimize2 size={15} /> : <FiMaximize2 size={15} />}
          </button>
        </div>
      </header>

      <div className="vx-scroll">
        {/* ── Version Rail ── */}
        <section className="vx-rail">
          <div className="vx-rail__head">
            <div className="vx-rail__intro">
              <h2 className="vx-rail__title">
                <FiGitBranch size={15} />
                {t('versionCompare.pickTitle')}
              </h2>
              <p className="vx-rail__hint">{t('versionCompare.pickHint', { max: MAX_SELECTED })}</p>
            </div>
            <div className="vx-rail__actions">
              <span className="vx-rail__counter">
                {t('versionCompare.selectedOf', { count: selected.length, max: MAX_SELECTED })}
              </span>
              <button type="button" className="vx-chip-btn" onClick={selectLatestChange} disabled={versions.length < 2}>
                <FiZap size={12} />
                {t('versionCompare.quickLatest')}
              </button>
              <button type="button" className="vx-chip-btn" onClick={selectFullSpan} disabled={versions.length < 2}>
                <FiGitBranch size={12} />
                {t('versionCompare.quickFull')}
              </button>
              {selected.length > 0 && (
                <button type="button" className="vx-chip-btn vx-chip-btn--ghost" onClick={() => setSelected([])}>
                  <FiX size={12} />
                  {t('versionCompare.clear')}
                </button>
              )}
            </div>
          </div>

          <div className="vx-rail__scroller">
            <div className="vx-rail__track" />
            {versionsLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="vx-vcard vx-vcard--skeleton" aria-hidden="true">
                    <div className="vx-skeleton vx-skeleton--title" />
                    <div className="vx-skeleton vx-skeleton--line" />
                    <div className="vx-skeleton vx-skeleton--line vx-skeleton--short" />
                  </div>
                ))
              : sortedVersions.map((ver) => (
                  <VersionCard
                    key={ver.versionNumber}
                    ver={ver}
                    isSelected={selected.includes(ver.versionNumber)}
                    orderIdx={sortedSelected.indexOf(ver.versionNumber)}
                    isLatest={ver.versionNumber === latestVersionNum}
                    onToggle={() => toggleVersion(ver.versionNumber)}
                    t={t}
                  />
                ))}
          </div>
        </section>

        {/* ── Journey Chain ── */}
        {pairs.length > 0 && (
          <section className="vx-chain">
            <div className="vx-chain__path">
              {sortedSelected.map((n, i) => (
                <React.Fragment key={n}>
                  <div className="vx-chain__node">
                    <span className="vx-chain__node-letter">{ORDER_LETTERS[i]}</span>
                    <span className="vx-chain__node-ver">{t('versionCompare.vShort', { n })}</span>
                  </div>
                  {i < sortedSelected.length - 1 && (
                    <button
                      type="button"
                      className="vx-chain__link"
                      onClick={() => scrollToStep(pairKey(n, sortedSelected[i + 1]))}
                      title={t('versionCompare.step', { n: i + 1 })}
                    >
                      {(() => {
                        const d = diffs[pairKey(n, sortedSelected[i + 1])];
                        if (!d || d.status === 'loading')
                          return <span className="vx-chain__spinner" aria-label={t('versionCompare.comparing')} />;
                        if (d.status === 'error') return <FiAlertCircle size={12} className="vx-chain__err" />;
                        return <StatPills stats={d.stats} t={t} compact />;
                      })()}
                      <FiArrowRight size={13} className="vx-chain__arrow" />
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="vx-chain__meta">
              {!anyLoading && (
                <span className="vx-chain__total">
                  <strong>{totalChanges}</strong> {t('versionCompare.totalChangesLabel')}
                </span>
              )}
              <span className="vx-chain__kbd-hint">
                <kbd>↑</kbd>
                <kbd>↓</kbd>
                {t('versionCompare.keyboardHint')}
              </span>
            </div>
          </section>
        )}

        {/* ── Main Content ── */}
        <main className="vx-main">
          {!versionsLoading && versions.length < 2 ? (
            <div className="vx-empty">
              <div className="vx-empty__icon">
                <FiGitBranch size={30} />
              </div>
              <h3>{t('versionCompare.onlyOneVersionTitle')}</h3>
              <p>{t('versionCompare.onlyOneVersionDesc')}</p>
            </div>
          ) : !versionsLoading && selected.length < 2 ? (
            <div className="vx-empty">
              <div className="vx-empty__icon">
                <FiLayers size={30} />
              </div>
              <h3>{t('versionCompare.selectMoreTitle')}</h3>
              <p>{t('versionCompare.selectMoreDesc', { max: MAX_SELECTED })}</p>
            </div>
          ) : (
            steps.map((step, si) => {
              const fromInfo = versionInfo(step.from);
              const toInfo = versionInfo(step.to);
              const diff = step.diff;

              return (
                <section
                  key={step.key}
                  className="vx-step"
                  ref={(el) => (stepRefs.current[step.key] = el)}
                >
                  <header className="vx-step__header">
                    <div className="vx-step__header-left">
                      {steps.length > 1 && <span className="vx-step__num">{si + 1}</span>}
                      <div className="vx-step__route">
                        <span className="vx-step__ver vx-step__ver--from">
                          {t('versionCompare.versionN', { n: step.from })}
                        </span>
                        <FiArrowRight size={14} className="vx-step__route-arrow" />
                        <span className="vx-step__ver vx-step__ver--to">
                          {t('versionCompare.versionN', { n: step.to })}
                        </span>
                      </div>
                      <div className="vx-step__authors">
                        {toInfo?.createdBy && (
                          <span className="vx-step__author">
                            <UserAvatar user={toInfo.createdBy} size="xs" />
                            {getFullName(toInfo.createdBy)}
                          </span>
                        )}
                        {toInfo?.createdAt && (
                          <span className="vx-step__date">
                            <FiClock size={10} />
                            {formatDate(toInfo.createdAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="vx-step__header-right">
                      {diff?.status === 'ready' && <StatPills stats={diff.stats} t={t} />}
                    </div>
                  </header>

                  {!diff || diff.status === 'loading' ? (
                    <div className="vx-step__body">
                      <SkeletonCard />
                      <SkeletonCard />
                    </div>
                  ) : diff.status === 'error' ? (
                    <div className="vx-step__error">
                      <FiAlertCircle size={18} />
                      <span>{diff.error || t('versionCompare.compareFailed')}</span>
                      <button
                        type="button"
                        className="vx-chip-btn"
                        onClick={() => loadPair(step.from, step.to)}
                      >
                        <FiRefreshCw size={12} />
                        {t('versionCompare.retry')}
                      </button>
                    </div>
                  ) : diff.blocks.length === 0 ? (
                    <div className="vx-step__identical">
                      <FiCheckCircle size={16} />
                      <span>
                        {t('versionCompare.identicalDesc', { v1: step.from, v2: step.to })}
                      </span>
                    </div>
                  ) : (
                    <div className="vx-step__body">
                      {diff.blocks.map((block, bi) => {
                        const globalIdx = stepStartIdx[si] + bi;
                        const refKey = `${step.key}:${block.id}`;
                        return (
                          <ChangeCard
                            key={refKey}
                            block={block}
                            num={bi + 1}
                            active={globalIdx === activeIdx}
                            viewMode={viewMode}
                            fromV={step.from}
                            toV={step.to}
                            onActivate={() => setActiveIdx(globalIdx)}
                            cardRef={(el) => (changeRefs.current[refKey] = el)}
                            animIdx={bi}
                            t={t}
                          />
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </main>
      </div>

      {/* ── Floating Navigator ── */}
      {totalChanges > 0 && (
        <div className="vx-nav">
          <button
            type="button"
            className="vx-nav__btn"
            onClick={() => navigateTo(activeIdx - 1)}
            disabled={activeIdx === 0}
            aria-label={t('versionCompare.prevChange')}
            title={t('versionCompare.prevChange')}
          >
            <FiChevronUp size={18} />
          </button>
          <div className="vx-nav__counter">
            <span className="vx-nav__label">{t('versionCompare.change')}</span>
            <span className="vx-nav__num">
              {activeIdx + 1} <span className="vx-nav__sep">{t('versionCompare.of')}</span>{' '}
              {totalChanges}
            </span>
          </div>
          <button
            type="button"
            className="vx-nav__btn"
            onClick={() => navigateTo(activeIdx + 1)}
            disabled={activeIdx >= totalChanges - 1}
            aria-label={t('versionCompare.nextChange')}
            title={t('versionCompare.nextChange')}
          >
            <FiChevronDown size={18} />
          </button>
        </div>
      )}

      {/* ── Aggregate summary strip (bottom-left, only for multi-step) ── */}
      {steps.length > 1 && !anyLoading && totalChanges > 0 && (
        <div className="vx-total-float">
          <StatPills stats={totalStats} t={t} />
        </div>
      )}
    </div>
  );
}

export default VersionCompare;
